import { Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import * as sx from "./agent-welcome.sx";
import { WelcomeMessage } from "./welcome-message";

export interface AgentOption {
  label: string;
  value: string;
}

// Interim frontend map of agent `key` -> i18n copy slug. Until the agent
// manifest carries its own `description`, the per-agent copy lives in en.json
// (catalyst.agent.{description,example}.<slug>). Agents not in this map render
// with just their name.
type AgentCopySlug =
  | "candidate-analysis"
  | "data-analysis"
  | "guided-project-candidates"
  | "project-first-candidates";

const COPY_SLUG_BY_KEY: Partial<Record<string, AgentCopySlug>> = {
  "candidate-analyst": "candidate-analysis",
  "data-analyst": "data-analysis",
  guided: "guided-project-candidates",
  oneshot: "project-first-candidates",
};

export function AgentWelcome({
  activeAgentKey,
  agents,
  onSelectAgent,
}: {
  activeAgentKey: string;
  agents: AgentOption[];
  onSelectAgent: (key: string) => void;
}) {
  const { formatMessage } = useIntl();

  return (
    <WelcomeMessage
      align="top"
      body={<FormattedMessage id="catalyst.agent.welcome.intro" />}
      title={<FormattedMessage id="catalyst.welcome.title" />}
    >
      {agents.length ? (
        // A group of toggle buttons rather than a radiogroup: each card switches
        // the active agent on click (an instant action, not a select-then-submit
        // choice). `aria-pressed` marks the current one; single-select is enforced
        // by state, not radio semantics.
        <sx.AgentList
          aria-label={formatMessage({
            id: "catalyst.agent.welcome.picker-label",
          })}
          role="group"
        >
          {agents.map((agent) => {
            const slug = COPY_SLUG_BY_KEY[agent.value];
            const isActive = agent.value === activeAgentKey;
            return (
              <sx.AgentCard
                key={agent.value}
                aria-pressed={isActive}
                fullWidth
                isActive={isActive}
                // Selecting resets the conversation; safe because the picker only
                // renders in the empty state (no messages to lose).
                onClick={() => onSelectAgent(agent.value)}
                variant="contained"
              >
                <sx.AgentName variant="subtitle1">{agent.label}</sx.AgentName>
                {slug ? (
                  <Typography variant="body1">
                    {formatMessage({
                      id: `catalyst.agent.description.${slug}`,
                    })}
                  </Typography>
                ) : null}
                {/* The example is heavier copy, so only the selected agent shows
                    it — it previews as you switch between agents. */}
                {slug && isActive ? (
                  <sx.AgentExample variant="body1">
                    <FormattedMessage
                      id="catalyst.agent.welcome.example"
                      values={{
                        prompt: formatMessage({
                          id: `catalyst.agent.example.${slug}`,
                        }),
                      }}
                    />
                  </sx.AgentExample>
                ) : null}
              </sx.AgentCard>
            );
          })}
        </sx.AgentList>
      ) : null}
    </WelcomeMessage>
  );
}
